import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Heart, ArrowRight } from 'lucide-react';
import { BreadcrumbSchema } from './BreadcrumbSchema';
import { TopicalClusters } from './TopicalClusters';

export interface FAQItem {
  q: string;
  a: string;
}

interface SEOPageLayoutProps {
  title: string;
  metaDescription: string;
  h1: string;
  subtitle?: string;
  children: React.ReactNode;
  faq: FAQItem[];
  relatedPages?: { label: string; href: string }[];
}

export function SEOPageLayout({ title, metaDescription, h1, subtitle, children, faq, relatedPages }: SEOPageLayoutProps) {
  const path = typeof window !== 'undefined' ? window.location.pathname : undefined;
  useDocumentHead({
    title,
    description: metaDescription,
    path,
    meta: [
      { property: 'og:type', content: 'article' },
      { name: 'robots', content: 'index, follow' },
    ],
  });

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <BreadcrumbSchema
        items={[
          { name: 'Home', path: '/' },
          { name: h1, path: path ?? '/' },
        ]}
      />
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="container max-w-3xl py-12 md:py-20">
          <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground leading-tight mb-4">{h1}</h1>
          {subtitle && <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">{subtitle}</p>}
        </section>

        {/* Body content */}
        <section className="container max-w-3xl pb-12 prose prose-lg dark:prose-invert prose-headings:font-heading prose-a:text-primary hover:prose-a:text-primary/80">
          {children}
        </section>

        {/* Mid-page CTA */}
        <section className="bg-primary/5 dark:bg-primary/10 py-12">
          <div className="container max-w-3xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground mb-4">Ready to say what matters?</h2>
            <p className="text-muted-foreground mb-6">Create a heartfelt message in minutes — free, private, and meaningful.</p>
            <Button asChild size="lg" className="gap-2">
              <Link to="/signup"><Heart className="w-4 h-4" /> Create Your Free Message</Link>
            </Button>
          </div>
        </section>

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="container max-w-3xl py-12">
            <h2 className="text-2xl font-bold font-heading text-foreground mb-6">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-foreground">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
          </section>
        )}

        {/* Related pages — page-specific quick links */}
        {relatedPages && relatedPages.length > 0 && (
          <section className="container max-w-3xl pb-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">Explore More</h3>
            <div className="flex flex-wrap gap-3">
              {relatedPages.map(p => (
                <Link key={p.href} to={p.href} className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 underline underline-offset-2">
                  {p.label} <ArrowRight className="w-3 h-3" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Topical clusters + secondary CTAs — strengthen topical authority */}
        <TopicalClusters currentPath={path} />


        {/* Final CTA */}
        <section className="bg-primary dark:bg-primary/90 py-16">
          <div className="container max-w-3xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">Don't wait for the perfect moment</h2>
            <p className="text-primary-foreground/80 mb-6">The people you love deserve to hear it while they still can.</p>
            <Button asChild size="lg" variant="secondary" className="gap-2">
              <Link to="/signup">Start Writing Now <ArrowRight className="w-4 h-4" /></Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
